import { Checkbox, Form, Input } from 'antd'
import {
  Controller,
  useWatch,
  type Control,
  type FieldPath,
  type FieldValues,
  type UseFormClearErrors,
  type UseFormSetError,
  type UseFormSetValue
} from 'react-hook-form'

import {
  BasicTd,
  BasicTh,
  BasicTr,
  NormalizedInputNumber
} from '@/components/elements'
import { LABELS } from '@/constants/common/message.constants'

import {
  WORK_MINUTES_MAX,
  WORK_MINUTES_MIN
} from '../../models/shipment-order-cargo-handling-form-value'

type Props<
  TFieldValues extends FieldValues,
  TLoadingOutsourced extends FieldPath<TFieldValues>,
  TLoadingMinutes extends FieldPath<TFieldValues>,
  TLoadingNote extends FieldPath<TFieldValues>,
  TUnloadingOutsourced extends FieldPath<TFieldValues>,
  TUnloadingMinutes extends FieldPath<TFieldValues>,
  TUnloadingNote extends FieldPath<TFieldValues>
> = {
  control: Control<TFieldValues>
  loadingOutsourcedName: TLoadingOutsourced
  loadingMinutesName: TLoadingMinutes
  loadingNoteName: TLoadingNote
  unloadingOutsourcedName: TUnloadingOutsourced
  unloadingMinutesName: TUnloadingMinutes
  unloadingNoteName: TUnloadingNote
  /**
   * 編集可否。未配車編集・配車後編集モーダルの `canEditFields` を渡す。
   * 作成系モーダルは未指定（= 編集可）でよい。
   */
  isEditing?: boolean
  /**
   * 予定作業時間の範囲外・非整数入力を保存前に止めるために使う。
   * `NormalizedInputNumber` は不正値を `onChange` へ渡さず表示だけ保持するため、
   * これらを受け取らないと「入力したのに保存値は元のまま」で保存できてしまう
   * (請求運賃など既存の数値入力と同じ 2 段構えにする)。
   */
  setError?: UseFormSetError<TFieldValues>
  clearErrors?: UseFormClearErrors<TFieldValues>
  /**
   * 委託なしへ戻したときに予定作業時間と備考を消すために使う。
   * 渡さないと、画面に出ていない値がそのまま保存される。
   */
  setValue?: UseFormSetValue<TFieldValues>
}

/**
 * 配送依頼 (ShipmentOrder) の荷役作業セクション。
 *
 * 積込み・取卸しそれぞれの「作業の委託有無」「予定作業時間(分)」「備考」を入力する。
 * 標準貨物自動車運送約款にもとづく書面化項目であり、鴻池運輸の全社共通 FMT
 * 「2.荷役作業・附帯業務等」に対応する。
 *
 * 4本の配送依頼モーダル（作成 / 同時作成 / 未配車編集 / 配車後編集）から共通で使う。
 * 配車後編集モーダルはフィールドパスが `shipments.${index}.*` とネストするため、
 * `FieldPath` ジェネリクスで名前を受け取る（`libs/hooks/use-follow-delivery-by-shipping.ts`
 * と同じ流儀。キャストは不要）。
 *
 * レビュー指摘（#4938, Biz確認済み）によりセクション自体は常時表示にした。
 * 予定作業時間と備考 (#4893) は、**委託ありをチェックしたときだけ**入力欄を出す。
 * 委託しない作業に予定時間も申し送りも要らないという Biz 判断による
 * （#4893 でマツケンに確認、選択肢 B）。当初は備考だけ常時表示にしていたが、
 * 自社作業の申し送りは配送依頼の備考欄へ書く運用で足りると確定した。
 *
 * 委託なしへ戻したときは予定作業時間と備考の値も消す。入力欄は
 * アンマウントされるが RHF の値は残るため、消さないと画面のどこにも出ない値が
 * そのまま保存される。帳票 (#4751) は委託なしを「無」と印字するので、
 * 「無」と「30分」が同じ行に並ぶ矛盾した印字になる。
 */
export const ShipmentOrderCargoHandlingSection = <
  TFieldValues extends FieldValues,
  TLoadingOutsourced extends FieldPath<TFieldValues>,
  TLoadingMinutes extends FieldPath<TFieldValues>,
  TLoadingNote extends FieldPath<TFieldValues>,
  TUnloadingOutsourced extends FieldPath<TFieldValues>,
  TUnloadingMinutes extends FieldPath<TFieldValues>,
  TUnloadingNote extends FieldPath<TFieldValues>
>({
  control,
  loadingOutsourcedName,
  loadingMinutesName,
  loadingNoteName,
  unloadingOutsourcedName,
  unloadingMinutesName,
  unloadingNoteName,
  isEditing = true,
  setError,
  clearErrors,
  setValue
}: Props<
  TFieldValues,
  TLoadingOutsourced,
  TLoadingMinutes,
  TLoadingNote,
  TUnloadingOutsourced,
  TUnloadingMinutes,
  TUnloadingNote
>) => {
  const loadingOutsourced = useWatch({ control, name: loadingOutsourcedName })
  const unloadingOutsourced = useWatch({
    control,
    name: unloadingOutsourcedName
  })

  const rows = [
    {
      label: LABELS.LOADING_WORK,
      outsourcedName: loadingOutsourcedName,
      minutesName: loadingMinutesName,
      noteName: loadingNoteName,
      isOutsourced: loadingOutsourced === true
    },
    {
      label: LABELS.UNLOADING_WORK,
      outsourcedName: unloadingOutsourcedName,
      minutesName: unloadingMinutesName,
      noteName: unloadingNoteName,
      isOutsourced: unloadingOutsourced === true
    }
  ]

  return (
    <>
      {rows.map(
        ({ label, outsourcedName, minutesName, noteName, isOutsourced }) => (
          <BasicTr key={outsourcedName}>
            <BasicTh>{label}</BasicTh>
            <BasicTd colSpan={3}>
              <div className="flex flex-wrap items-center gap-2">
                {/* 委託の有無。既定は「無」= チェックなし */}
                <Controller
                  control={control}
                  name={outsourcedName}
                  render={({ field }) => (
                    <Checkbox
                      id={field.name}
                      checked={field.value === true}
                      disabled={!isEditing}
                      onChange={(e) => {
                        field.onChange(e.target.checked)
                        if (!e.target.checked) {
                          // 委託なしに戻すと予定作業時間の入力欄がアンマウントされる。
                          // 範囲外値で立てた手動エラーが残ったままだと、入力欄が
                          // 二度と出せないのに保存ボタンだけ無効のままになる。
                          clearErrors?.(minutesName)
                          // 値も消す。アンマウントしても RHF には残るため、消さないと
                          // 画面のどこにも出ない値がそのまま保存される。帳票 (#4751) は
                          // 委託なしを「無」と印字するので、「無」と「30分」が同じ行に
                          // 並ぶ矛盾した印字になる。
                          setValue?.(minutesName, null as never, {
                            shouldDirty: true
                          })
                          setValue?.(noteName, null as never, {
                            shouldDirty: true
                          })
                        }
                      }}
                    >
                      {LABELS.WORK_OUTSOURCED}
                    </Checkbox>
                  )}
                />
                {/* 予定作業時間。委託ありのときだけ入力欄を表示する（Biz確認済み） */}
                {isOutsourced && (
                  <>
                    <label htmlFor={minutesName}>
                      {LABELS.WORK_MINUTES_LABEL}
                    </label>
                    <Controller
                      control={control}
                      name={minutesName}
                      render={({ field, fieldState: { error } }) => (
                        <Form.Item
                          htmlFor={field.name}
                          validateStatus={error?.message ? 'error' : undefined}
                          help={error?.message}
                          className="m-0"
                        >
                          <NormalizedInputNumber
                            id={field.name}
                            value={field.value ?? null}
                            disabled={!isEditing}
                            min={WORK_MINUTES_MIN}
                            max={WORK_MINUTES_MAX}
                            step={1}
                            suffix={LABELS.WORK_MINUTES_UNIT}
                            placeholder="0"
                            className="w-[110px] text-left"
                            onChange={(value) => field.onChange(value ?? null)}
                            onInvalidValue={(message) =>
                              setError?.(minutesName, {
                                type: 'manual',
                                message
                              })
                            }
                            onValid={() => clearErrors?.(minutesName)}
                          />
                        </Form.Item>
                      )}
                    />
                    <label htmlFor={noteName}>{LABELS.WORK_NOTE_LABEL}</label>
                    <Controller
                      control={control}
                      name={noteName}
                      render={({ field }) => (
                        <Input
                          id={field.name}
                          value={field.value ?? ''}
                          disabled={!isEditing}
                          className="w-[240px]"
                          // 空欄は null で保存する。空文字のまま送ると「未入力」と
                          // 「空文字を明示的に入れた」が DB 上で区別できなくなる。
                          onChange={(e) =>
                            field.onChange(e.target.value || null)
                          }
                        />
                      )}
                    />
                  </>
                )}
              </div>
            </BasicTd>
          </BasicTr>
        )
      )}
    </>
  )
}
